import {action, observable, reaction} from 'mobx';

import {type CachedMediaFactory} from '../../../core/models/CachedMedia/CachedMedia.factory';
import {platform} from '../../../utils/platform';
import {FlagManager} from '../../../utils/store';
import {WithCallbacks} from '../../../utils/store/WithCallbacks';
import {isDefined} from '../../../utils/typecheck';
import {BaseVideoPlayerState} from './BaseVideoPlayerState';
import {VideoPlayerStateDomListenersManager} from './VideoPlayerStateDomListenersManager';

export class VideoPlayerState extends BaseVideoPlayerState<
  BaseVideoPlayerState.DOMPlayer,
  BaseVideoPlayerState.DOMContainer
> {
  callbacks = new WithCallbacks.Manager<
    BaseVideoPlayerState<
      BaseVideoPlayerState.DOMPlayer,
      BaseVideoPlayerState.DOMContainer
    >
  >();
  currentTime = 0;
  private isMutedFlag = new FlagManager(false);
  private listeners = new VideoPlayerStateDomListenersManager(this);

  constructor(
    props: BaseVideoPlayerState.Props,
    cachedMediaFactory: CachedMediaFactory,
  ) {
    super(props, cachedMediaFactory);

    this.bindHandlers();
    this.initCallbacks(props);

    BaseVideoPlayerState.makeObservable<VideoPlayerState>(this, {
      currentTime: observable,
      updateVolume: action.bound,
    });

    this.setupReactions();
  }

  override destruct() {
    super.destruct();
    this.listeners.isMetadataLoaded.setFalse();
    this.listeners.destruct();
  }

  get isEnded() {
    return this.listeners.isEndedFlag.value;
  }

  get isLoaded() {
    return (
      this.isMediaUrlReady &&
      this.listeners.isMetadataLoaded.isTrue &&
      // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
      [1, 3, 4].includes(this.player?.readyState || 0)
    );
  }

  get isMobile() {
    return platform.isMobileBrowser;
  }

  get isMuted() {
    return this.isMutedFlag.value;
  }

  get isPaused() {
    return this.listeners.isPausedFlag.value;
  }

  get shouldShowControls() {
    if (this.isMobile) {
      // return this.isPaused || this.isEnded;
      return true;
    }

    return this.listeners.shouldShowControlsFlag.value;
  }

  get totalTime() {
    return this.listeners.isMetadataLoaded.isTrue && this.player?.duration
      ? Number(this.player.duration.toFixed(2))
      : 0;
  }

  override onError(event: ErrorEvent) {
    const target = event.target as HTMLVideoElement;

    if (target.error) {
      const message = `Video error! Code: ${target.error.code}; details: ${target.error.message}`;

      super.onError(new Error(message));
    }
  }

  pause() {
    this.player?.pause();
  }

  play() {
    this.player?.play();
  }

  seek(time: number) {
    if (isDefined(this.player)) {
      this.player.currentTime = time;
    }
  }

  toggleFullscreen() {
    if (!this.allowFullscreen) {
      return;
    }

    const videoContainer = this.container;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (document.webkitFullscreenElement) {
      document.webkitExitFullscreen?.();
    } else if (videoContainer?.webkitRequestFullscreen) {
      videoContainer.webkitRequestFullscreen();
    } else if (videoContainer?.requestFullscreen) {
      videoContainer.requestFullscreen();
    }
  }

  toggleMute() {
    this.isMutedFlag.toggle();
    this.updateVolume(this.isMutedFlag.isTrue ? 0 : 0.5, false);
  }

  togglePlay() {
    if (this.isPaused || this.isEnded) {
      this.play();
    } else {
      this.pause();
    }
  }

  updateVolume(value: number, shouldDisableMute = true) {
    if (shouldDisableMute && this.isMutedFlag.isTrue) {
      this.isMutedFlag.setFalse();
    }

    this.volume = value;
  }

  override volumeChangeHandler(value: number) {
    this.updateVolume(value);
  }

  protected override prepareContainer() {
    this.listeners.initContainer();
  }

  protected preparePlayer() {
    this.listeners.initPlayer();
  }

  protected override setupReactions() {
    super.setupReactions();
    this.disposer.watch(
      'volume',
      reaction(
        () => this.volume,
        volume => {
          if (this.player) {
            this.player.volume = volume;
          }
        },
      ),
    );
  }

  private bindHandlers() {
    this.onError = this.onError.bind(this);
    this.volumeChangeHandler = this.volumeChangeHandler.bind(this);
  }
}
